import {
  runtimeApprenticeUrl,
  runtimeBackofficeUrl,
  runtimeLumiDomain,
  runtimeLumiProtocol,
  runtimePeopleUrl,
  runtimeTimeTrackingUrl,
  currentTenantSlug,
} from './runtime-config';
import { readDemoPersona } from '@lumi/app-kit';
import { storedUserId } from './stored-user';

// Canonical peer-module routing (single source of truth): prod path segment +
// local dev port. Cross-module URL building here and the in-module switcher
// (moduleSwitcher.ts) both read this table so the two never drift.
export const MODULE_ROUTING: Record<'ats' | 'time' | 'apprentice' | 'people', { segment: string; devPort: string }> = {
  ats: { segment: 'backoffice', devPort: '5173' },
  time: { segment: 'time', devPort: '5176' },
  apprentice: { segment: 'apprentice', devPort: '5178' },
  people: { segment: 'people', devPort: '5181' },
};

function routing(key: keyof typeof MODULE_ROUTING): { segment: string; local: string } {
  const { segment, devPort } = MODULE_ROUTING[key];
  return { segment, local: `http://localhost:${devPort}` };
}

// Maps both Area sender keys ('ats', 'timecard', …) and module path segments
// ('backoffice', 'time', …) to their base URL, so a resolved callToAction
// { module, deepLink } can be turned into an absolute cross-module URL. Mirrors
// WorkspaceView's buildModuleUrl. 'system'/unknown → null (stay in workspace).
const MODULES: Record<string, { url: () => string; segment: string; local: string }> = {
  ats: { url: runtimeBackofficeUrl, ...routing('ats') },
  backoffice: { url: runtimeBackofficeUrl, ...routing('ats') },
  timecard: { url: runtimeTimeTrackingUrl, ...routing('time') },
  time: { url: runtimeTimeTrackingUrl, ...routing('time') },
  apprentice: { url: runtimeApprenticeUrl, ...routing('apprentice') },
  people: { url: runtimePeopleUrl, ...routing('people') },
};

// Base domain = the current per-tenant host with its first (tenant) label removed,
// e.g. `tekay.lumi-hr.com` → `lumi-hr.com`. Only meaningful on a per-tenant host.
function baseDomainFromHost(): string {
  if (typeof window === 'undefined') return '';
  const labels = window.location.hostname.toLowerCase().split('.');
  return labels.length < 3 ? '' : labels.slice(1).join('.');
}

// A tenant's own workspace host (`{slug}.{domain}`). Used by the tenant switcher to
// redirect across tenants in prod: each tenant is its own host/DB, so switching =
// a full-page SSO handoff to that host, where the fan-out is same-origin + live.
export function tenantWorkspaceUrl(slug: string): string {
  const domain = runtimeLumiDomain();
  const resolved = domain && !domain.includes('{') && !domain.includes('}') ? domain : baseDomainFromHost();
  if (resolved) return `${runtimeLumiProtocol()}://${slug}.${resolved}`;
  return typeof window !== 'undefined' ? window.location.origin : '';
}

// The public marketing site (`www.{domain}`) — where Lumi's own imprint and
// privacy notice live (issue 221). Resolved the same way as every sibling
// cross-host builder: the configured LUMI_DOMAIN first, else the current
// per-tenant host with its tenant label stripped, else the local dev port. Empty
// only when there is no domain at all to point at, in which case the shared
// LegalLinksFooter renders nothing rather than a broken link.
export function landingBaseUrl(): string {
  const domain = runtimeLumiDomain();
  const resolved = domain && !domain.includes('{') && !domain.includes('}') ? domain : baseDomainFromHost();
  if (resolved) return `${runtimeLumiProtocol()}://www.${resolved}`;
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') return 'http://localhost:5175';
  return '';
}

// The data subject's self-service host (`account.{domain}`) — ARCHITECTURE.md:
// tenant-less on purpose, because the people it serves may hold rights in a
// workspace they can no longer sign into. It carries the Art. 15(3) takeout, the
// Art. 17 erasure request and the invoice list a suspended workspace still owes
// its owner, which is exactly what the workspace-unreachable page has to hand
// over (issue 364 F57). Resolved like every sibling cross-host builder above:
// the configured LUMI_DOMAIN first, else the current per-tenant host with its
// tenant label stripped. Empty when there is no domain at all, in which case the
// page renders no link rather than one into the void.
export function accountBaseUrl(): string {
  const domain = runtimeLumiDomain();
  const resolved = domain && !domain.includes('{') && !domain.includes('}') ? domain : baseDomainFromHost();
  if (resolved) return `${runtimeLumiProtocol()}://account.${resolved}`;
  return '';
}

// A configured module URL is unusable if it still carries an unresolved
// placeholder or doesn't parse — reject it and fall back to a derived host rather
// than navigate somewhere broken. Mirrors WorkspaceView's looksMalformedModuleUrl.
function looksMalformedUrl(value: string): boolean {
  const raw = String(value || '').toLowerCase();
  if (!raw) return true;
  if (raw.includes('{') || raw.includes('}') || raw.includes('%7b') || raw.includes('%7d')) return true;
  try {
    new URL(value);
    return false;
  } catch {
    return true;
  }
}

// Append the cross-app session handoff to a target URL, in place. Access/refresh
// tokens ride the URL ONLY when the target is cross-origin (local dev ports,
// another tenant's host) — a same-origin target (prod
// path routing) reads the shared per-origin localStorage directly, and tokens in
// a same-origin URL are pure leakage (browser history, server logs), which
// ADR 0024 forbids. Non-secret context params are appended unconditionally.
// `forceTokens` is kept for a caller that KNOWS its target holds its session
// under a different key and therefore needs the handoff even same-origin; the
// one target that did (the retired tenant admin app) is gone with #432, so
// nothing passes it today.
export function appendSessionHandoff(
  url: URL,
  session: { token?: string | null; refreshToken?: string | null },
  context: Record<string, string | null | undefined> = {},
  opts: { forceTokens?: boolean } = {},
): URL {
  const sameOrigin = typeof window !== 'undefined' && url.origin === window.location.origin;
  if (!sameOrigin || opts.forceTokens) {
    if (session.token) url.searchParams.set('token', session.token);
    if (session.refreshToken) url.searchParams.set('refreshToken', session.refreshToken);
  }
  for (const [name, value] of Object.entries(context)) {
    if (value) url.searchParams.set(name, value);
  }
  return url;
}

function moduleBaseUrl(key: string): string | null {
  // 'workspace' targets this app's own origin (e.g. support tickets live at
  // /support/t/:slug/:id in workspace-ui) — navigate within the workspace.
  if (key === 'workspace') {
    return typeof window !== 'undefined' ? window.location.origin : '';
  }
  const m = MODULES[key];
  if (!m) return null;
  const env = m.url();
  if (env) return env.includes('{tenant}') ? env.split('{tenant}').join(currentTenantSlug()) : env;
  const domain = runtimeLumiDomain();
  const slug = currentTenantSlug();
  if (domain && slug && !domain.includes('{')) {
    return `${runtimeLumiProtocol()}://${slug}.${domain}/${m.segment}`;
  }
  return m.local;
}

/** The session pair + non-secret context every cross-app handoff carries. */
export type SessionHandoff = { token?: string | null; refreshToken?: string | null };
export type HandoffContext = Record<string, string | null | undefined>;

// The handoff as this origin has it STORED — what a builder uses when no caller
// hands it live values (notification deep links, the post-login return).
function storedSession(): SessionHandoff {
  return {
    token: typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null,
    refreshToken: typeof localStorage !== 'undefined' ? localStorage.getItem('refreshToken') : null,
  };
}
function storedContext(slug = currentTenantSlug()): HandoffContext {
  return {
    tenantSlug: slug,
    locale: typeof localStorage !== 'undefined' ? localStorage.getItem('kb_locale') : null,
    // Same per-origin problem as the locale: the target module has its own
    // kb_theme, so an unforwarded theme means the deep link lands in the
    // wrong one (#364 F27).
    theme: typeof localStorage !== 'undefined' ? localStorage.getItem('kb_theme') : null,
    // And the demo persona (#409): a stamped read, so it is this user's choice
    // or nothing — never a mask left behind by the account before.
    demoPersona: readDemoPersona(slug, storedUserId()),
  };
}


// Build an absolute URL into a module at a root-relative deep link, carrying the
// SSO handoff params (tenantSlug / locale, plus tokens for cross-origin targets
// only — see appendSessionHandoff) so the landing module authenticates and lands
// on the deep-linked route (its router.beforeEach consumes ?token when present
// and preserves the path). `session`/`context` default to this origin's stored
// values; a reactive caller (the sidebar quicklinks, issue 401) passes the live ones.
// The shared body of the two builders below: join a base to a root-relative deep
// link and stamp the handoff on it. Extracted in #413 — the two were the same
// eleven lines apart from which base they resolve and whether tokens are forced.
function deepLinkUrl(
  base: string | null,
  deepLink: string,
  session: SessionHandoff,
  context: HandoffContext,
  opts?: { forceTokens?: boolean },
): string | null {
  if (!base) return null;
  const path = deepLink.startsWith('/') ? deepLink : `/${deepLink}`;
  let url: URL;
  try {
    url = new URL(base.replace(/\/$/, '') + path);
  } catch {
    return null;
  }
  appendSessionHandoff(url, session, context, opts);
  return url.toString();
}

export function moduleDeepLinkUrl(
  key: string,
  deepLink: string,
  session: SessionHandoff = storedSession(),
  context: HandoffContext = storedContext(),
): string | null {
  return deepLinkUrl(moduleBaseUrl(key), deepLink, session, context);
}

// ── Post-login "return to the module you came from" (single-login handoff) ──
//
// A module that bounces an unauthenticated user to this login carries
// `?module=<key>&returnPath=<deepLink>`; after a successful password/SSO login
// we send the user back there. Both values are attacker-influenced (they ride
// in the URL), so both are validated before we ever navigate — the open-redirect
// defense, mirroring user-api/oidc.js `isSafeReturnPath`.

// The module keys a return may target — the path-routed peers `moduleDeepLinkUrl`
// understands ('ats'/'backoffice', 'time'/'timecard', 'apprentice',
// 'people'). Anything else is rejected → homebase. `user` was on this list while
// the tenant admin app was its own host; its pages are routes of THIS app now
// (#432), so a return to them needs no cross-app hop at all.
const RETURN_MODULE_KEYS = new Set([
  'ats', 'backoffice', 'time', 'timecard', 'apprentice', 'people',
]);

// A returnPath is only safe if it's a root-relative in-app path: it must start
// with a single '/', and must not be protocol-relative ('//host') or a
// backslash trick ('/\\host') that browsers normalize to a cross-origin URL.
export function isSafeReturnPath(path: unknown): path is string {
  if (typeof path !== 'string' || !path) return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//') || path.startsWith('/\\')) return false;
  return true;
}

// Resolve the absolute URL to bounce to after login, or null when there's no
// valid module to return to (caller falls back to the workspace homebase). The
// URL carries the freshly-issued session (token/tenantSlug/locale), so this must
// be called AFTER the token is stored.
export function postLoginRedirectUrl(moduleKey: unknown, returnPath: unknown): string | null {
  if (typeof moduleKey !== 'string' || !RETURN_MODULE_KEYS.has(moduleKey)) return null;
  const path = isSafeReturnPath(returnPath) ? returnPath : '/';
  return moduleDeepLinkUrl(moduleKey, path);
}

// The other half of the same return-path slot (#445 M2): a WORKSPACE-INTERNAL
// deep link (e.g. a notification mail's `/notifications/:id/open`) carries no
// `module` at all — there is no cross-app hop to make, so `postLoginRedirectUrl`
// above answers null for it and the caller must fall back to `router.push`
// instead of a full-page `window.location.href`. The router's requiresAuth gate
// stamps `to.fullPath` onto `returnPath` for exactly this case (nothing else was
// already claiming the slot); a `module` present here means a cross-module
// return is what's wanted, so this returns null and leaves that to the sibling.
export function internalReturnPath(moduleKey: unknown, returnPath: unknown): string | null {
  if (typeof moduleKey === 'string' && RETURN_MODULE_KEYS.has(moduleKey)) return null;
  return isSafeReturnPath(returnPath) ? returnPath : null;
}
